// Badge definitions
export const BADGE_DEFINITIONS = {
  first_practice: { name: "First Steps", icon: "\u{1F3AF}", description: "Complete your first practice" },
  perfect_score: { name: "Perfect!", icon: "\u{1F4AF}", description: "Score 100% on any practice" },
  streak_3: { name: "On Fire", icon: "\u{1F525}", description: "3-day practice streak" },
  streak_7: { name: "Unstoppable", icon: "\u26A1", description: "7-day practice streak" },
  all_techniques: { name: "Explorer", icon: "\u{1F5FA}\uFE0F", description: "Try all 10 techniques" },
};

export const MEMORY_METHODS = {
  link: {
    name: "Link System",
    description: "Connect items through vivid mental associations",
    icon: "\u{1F517}",
    color: "from-blue-600 to-indigo-600"
  },
  number: {
    name: "Number Memorization",
    description: "Transform numbers into memorable forms",
    icon: "\u{1F522}",
    color: "from-purple-600 to-pink-600"
  },
  peg: {
    name: "Peg System",
    description: "Number-word associations (1=tie, 2=Noah, etc.)",
    icon: "\u{1F3AF}",
    color: "from-green-600 to-teal-600"
  },
  major: {
    name: "Major System",
    description: "Advanced number-to-consonant conversion system",
    icon: "\u{1F524}",
    color: "from-red-600 to-orange-600"
  },
  nameface: {
    name: "Name-Face Association",
    description: "Techniques for remembering people's names",
    icon: "\u{1F464}",
    color: "from-yellow-600 to-amber-600"
  },
  cards: {
    name: "Playing Card Memory",
    description: "Full deck memorization methods",
    icon: "\u{1F0CF}",
    color: "from-cyan-600 to-blue-600"
  },
  language: {
    name: "Foreign Language",
    description: "Memory techniques for vocabulary learning",
    icon: "\u{1F30D}",
    color: "from-violet-600 to-purple-600"
  },
  speech: {
    name: "Text & Speech Memory",
    description: "For presentations and speeches",
    icon: "\u{1F4D6}",
    color: "from-pink-600 to-rose-600"
  },
  palace: {
    name: "Memory Palace",
    description: "Spatial memory technique using locations",
    icon: "\u{1F3DB}\uFE0F",
    color: "from-emerald-600 to-green-600"
  },
  rhyme: {
    name: "Rhyme System",
    description: "Using rhymes for memorization",
    icon: "\u{1F3B5}",
    color: "from-indigo-600 to-blue-600"
  }
};

export const PRACTICE_TYPES = {
  link: {
    shopping: {
      name: "Shopping Lists",
      description: "Memorize grocery and shopping items",
      difficulty: "Beginner",
      items: [
        ["Milk", "Bread", "Eggs", "Bananas", "Cheese"],
        ["Apples", "Chicken", "Rice", "Tomatoes", "Yogurt"],
        ["Pasta", "Onions", "Butter", "Carrots", "Salmon"],
        ["Potatoes", "Broccoli", "Olive Oil", "Garlic", "Spinach"]
      ]
    },
    words: {
      name: "Random Words",
      description: "Connect unrelated words using associations",
      difficulty: "Intermediate",
      items: [
        ["Elephant", "Keyboard", "Mountain", "Purple", "Thunder"],
        ["Ocean", "Telescope", "Butterfly", "Clock", "Dragon"],
        ["Rainbow", "Guitar", "Castle", "Fire", "Diamond"],
        ["Forest", "Robot", "Moon", "Flower", "Magic"]
      ]
    },
    facts: {
      name: "Historical Facts",
      description: "Remember important dates and events",
      difficulty: "Advanced",
      items: [
        ["1776 Independence", "1969 Moon Landing", "1989 Berlin Wall", "2001 Internet Boom"],
        ["1492 Columbus", "1914 WWI Start", "1945 WWII End", "1991 Soviet Union End"],
        ["1066 Norman Conquest", "1215 Magna Carta", "1588 Spanish Armada", "1707 Act of Union"],
        ["753 BC Rome Founded", "44 BC Caesar Death", "476 AD Rome Falls", "1066 Hastings Battle"]
      ]
    },
    todo: {
      name: "To-Do Items",
      description: "Organize daily tasks and reminders",
      difficulty: "Beginner",
      items: [
        ["Call dentist", "Buy birthday gift", "Finish report", "Walk the dog", "Pay bills"],
        ["Email client", "Grocery shopping", "Gym workout", "Book restaurant", "Clean house"],
        ["Doctor appointment", "Car service", "Tax documents", "Plant garden", "Family dinner"],
        ["Update resume", "Bank meeting", "Oil change", "Birthday party", "Photo backup"]
      ]
    }
  },
  number: {
    phone: {
      name: "Phone Numbers",
      description: "Memorize important contact numbers",
      difficulty: "Intermediate",
      items: [
        ["555-0123", "555-0456", "555-0789"],
        ["555-1234", "555-5678", "555-9012"],
        ["555-2468", "555-1357", "555-3691"],
        ["555-7531", "555-9753", "555-8642"]
      ]
    },
    dates: {
      name: "Important Dates",
      description: "Remember birthdays and anniversaries",
      difficulty: "Beginner",
      items: [
        ["March 15, 1985", "July 4, 1776", "December 25, 2020"],
        ["January 1, 2000", "September 11, 2001", "May 8, 1945"],
        ["April 14, 1912", "November 22, 1963", "February 14, 1929"],
        ["October 12, 1492", "June 6, 1944", "August 15, 1947"]
      ]
    },
    sequences: {
      name: "Math Sequences",
      description: "Master mathematical number patterns",
      difficulty: "Advanced",
      items: [
        ["1, 1, 2, 3, 5, 8, 13", "2, 4, 8, 16, 32", "1, 4, 9, 16, 25"],
        ["3, 6, 12, 24, 48", "1, 3, 6, 10, 15", "2, 6, 18, 54, 162"],
        ["5, 10, 20, 40, 80", "1, 8, 27, 64, 125", "7, 14, 28, 56, 112"],
        ["11, 22, 33, 44, 55", "13, 26, 39, 52, 65", "17, 34, 51, 68, 85"]
      ]
    },
    random: {
      name: "Random Digits",
      description: "Pure memory challenge with digit strings",
      difficulty: "Expert",
      items: [
        ["4729", "83561", "147258", "9632587"],
        ["5836", "92748", "385179", "4729563"],
        ["7194", "65932", "741852", "8394756"],
        ["2847", "59637", "963741", "1847295"]
      ]
    }
  },
  peg: {
    basic: {
      name: "Basic Peg Words",
      description: "Learn the fundamental number-word pegs",
      difficulty: "Beginner",
      items: [
        ["1=Tie", "2=Noah", "3=Ma", "4=Rye", "5=Law"],
        ["6=Shoe", "7=Cow", "8=Foe", "9=Pie", "10=Toes"],
        ["11=Tot", "12=Tin", "13=Tomb", "14=Tire", "15=Tail"],
        ["16=Dish", "17=Tack", "18=Dove", "19=Tub", "20=Nose"]
      ]
    },
    items: {
      name: "Peg Item Lists",
      description: "Use pegs to memorize ordered lists",
      difficulty: "Intermediate",
      items: [
        ["Hammer", "Bicycle", "Coffee", "Sunglasses", "Book"],
        ["Phone", "Wallet", "Keys", "Notebook", "Pen"],
        ["Watch", "Camera", "Headphones", "Water Bottle", "Backpack"],
        ["Laptop", "Charger", "Mouse", "Cables", "Tablet"]
      ]
    },
    advanced: {
      name: "Advanced Pegs",
      description: "Extended peg system for longer lists",
      difficulty: "Advanced",
      items: [
        ["21=Net", "22=Nun", "23=Name", "24=Nero", "25=Nail"],
        ["26=Notch", "27=Neck", "28=Knife", "29=Knob", "30=Mouse"],
        ["31=Mat", "32=Moon", "33=Mummy", "34=Mower", "35=Mule"],
        ["36=Match", "37=Mug", "38=Movie", "39=Map", "40=Rose"]
      ]
    },
    history: {
      name: "Historical Pegs",
      description: "Use pegs for historical chronology",
      difficulty: "Expert",
      items: [
        ["1066 Battle of Hastings", "1215 Magna Carta", "1492 Columbus", "1776 Independence"],
        ["1789 French Revolution", "1815 Waterloo", "1869 Suez Canal", "1914 WWI Starts"],
        ["1929 Stock Crash", "1945 WWII Ends", "1969 Moon Landing", "1989 Berlin Wall"],
        ["1991 Internet Birth", "2001 9/11 Attacks", "2008 Financial Crisis", "2020 Pandemic"]
      ]
    }
  },
  major: {
    conversion: {
      name: "Number Conversion",
      description: "Convert numbers to consonant sounds",
      difficulty: "Beginner",
      items: [
        ["14 = T-R = Tire", "25 = N-L = Nail", "36 = M-J = Mage"],
        ["47 = R-K = Rock", "58 = L-F = Leaf", "69 = J-P = Jeep"],
        ["73 = G-M = Gum", "84 = F-R = Fire", "95 = P-L = Pail"],
        ["16 = T-J = Taj", "27 = N-K = Neck", "38 = M-F = Muff"]
      ]
    },
    words: {
      name: "Major System Words",
      description: "Master common major system words",
      difficulty: "Intermediate",
      items: [
        ["100 = T-S-S = Toss", "200 = N-S-S = Nose", "300 = M-S-S = Mass"],
        ["400 = R-S-S = Rose", "500 = L-S-S = Loss", "600 = J-S-S = Jazz"],
        ["700 = K-S-S = Kiss", "800 = F-S-S = Fuss", "900 = P-S-S = Pass"],
        ["1000 = T-S-S-S = Toss", "2000 = N-S-S-S = Noses", "3000 = M-S-S-S = Masses"]
      ]
    },
    sequences: {
      name: "Long Sequences",
      description: "Memorize extended number sequences",
      difficulty: "Advanced",
      items: [
        ["142857", "271828", "314159", "618034"],
        ["123456", "987654", "147258", "963741"],
        ["112358", "135792", "246810", "369258"],
        ["159753", "258147", "741963", "852741"]
      ]
    },
    pi: {
      name: "Pi Memorization",
      description: "Master digits of pi using Major System",
      difficulty: "Expert",
      items: [
        ["3.14159", "3.141592", "3.1415926", "3.14159265"],
        ["26535", "265358", "2653589", "26535897"],
        ["89793", "897932", "8979323", "89793238"],
        ["23846", "238462", "2384626", "23846264"]
      ]
    }
  },
  nameface: {
    basic: {
      name: "Basic Name Techniques",
      description: "Learn fundamental name association methods",
      difficulty: "Beginner",
      items: [
        ["John = Toilet", "Mary = Merry", "David = Divide", "Sarah = Sera"],
        ["Michael = Microphone", "Lisa = Mona Lisa", "Robert = Robot", "Jennifer = Juniper"],
        ["William = Will", "Elizabeth = Queen", "James = Jam", "Patricia = Patrick"],
        ["Christopher = Christ", "Barbara = Barb", "Daniel = Dan", "Nancy = Fancy"]
      ]
    },
    features: {
      name: "Facial Features",
      description: "Associate names with prominent features",
      difficulty: "Intermediate",
      items: [
        ["Big nose = Schnoz", "Curly hair = Curls", "Blue eyes = Sky", "Beard = Forest"],
        ["Dimples = Dots", "Glasses = Specs", "Smile = Sunshine", "Freckles = Stars"],
        ["Bald = Dome", "Mustache = Brush", "Wrinkles = Lines", "Scar = Mark"],
        ["Thick eyebrows = Bushes", "Gap teeth = Space", "Rosy cheeks = Apples", "Long neck = Giraffe"]
      ]
    },
    professions: {
      name: "Name-Profession Links",
      description: "Connect names with occupations",
      difficulty: "Advanced",
      items: [
        ["Dr. Smith = Blacksmith", "Baker Bill = Bread", "Teacher Jane = Brain", "Chef Carlos = Cook"],
        ["Nurse Nancy = Care", "Pilot Pete = Plane", "Artist Anna = Paint", "Lawyer Luke = Law"],
        ["Engineer Ed = Build", "Dentist Dan = Teeth", "Farmer Fred = Field", "Writer Will = Pen"],
        ["Banker Bob = Money", "Driver Dave = Wheel", "Singer Sue = Song", "Dancer Dee = Dance"]
      ]
    },
    advanced: {
      name: "Complex Associations",
      description: "Multi-layered name-face connections",
      difficulty: "Expert",
      items: [
        ["Margaret + Glasses + Teacher = Maggie Specs School", "Alexander + Beard + Doctor = Alex Hair Medicine"],
        ["Stephanie + Curls + Artist = Steph Spiral Paint", "Benjamin + Tall + Engineer = Ben Giant Build"],
        ["Katherine + Dimples + Lawyer = Kate Dots Law", "Nicholas + Smile + Chef = Nick Grin Cook"],
        ["Samantha + Freckles + Nurse = Sam Stars Care", "Jonathan + Mustache + Pilot = Jon Brush Fly"]
      ]
    }
  },
  cards: {
    suits: {
      name: "Suit Associations",
      description: "Master the four suit systems",
      difficulty: "Beginner",
      items: [
        ["Hearts = Love", "Diamonds = Wealth", "Clubs = War", "Spades = Death"],
        ["Ace Hearts = Love Leader", "King Diamonds = Rich King", "Queen Clubs = War Queen", "Jack Spades = Death Jack"],
        ["2 Hearts = Love Pair", "3 Diamonds = Rich Trio", "4 Clubs = War Squad", "5 Spades = Death Five"],
        ["6 Hearts = Love Six", "7 Diamonds = Lucky Rich", "8 Clubs = War Eight", "9 Spades = Death Nine"]
      ]
    },
    sequence: {
      name: "Card Sequences",
      description: "Memorize card order using stories",
      difficulty: "Intermediate",
      items: [
        ["Ace\u2660", "King\u2665", "Queen\u2666", "Jack\u2663", "10\u2660"],
        ["9\u2665", "8\u2666", "7\u2663", "6\u2660", "5\u2665"],
        ["4\u2666", "3\u2663", "2\u2660", "Ace\u2665", "King\u2666"],
        ["Queen\u2663", "Jack\u2660", "10\u2665", "9\u2666", "8\u2663"]
      ]
    },
    persona: {
      name: "Card Personalities",
      description: "Assign personalities to each card",
      difficulty: "Advanced",
      items: [
        ["King\u2660 = Stern King", "Queen\u2665 = Loving Queen", "Jack\u2666 = Rich Prince", "Ace\u2663 = War Ace"],
        ["10\u2660 = Dark Ten", "9\u2665 = Happy Nine", "8\u2666 = Wealthy Eight", "7\u2663 = Battle Seven"],
        ["6\u2660 = Sad Six", "5\u2665 = Joyful Five", "4\u2666 = Gem Four", "3\u2663 = Fight Three"],
        ["2\u2660 = Gloomy Two", "Ace\u2665 = Love Ace", "King\u2666 = Gold King", "Queen\u2663 = Warrior Queen"]
      ]
    },
    fulldeck: {
      name: "Full Deck Challenge",
      description: "Memorize complete shuffled deck",
      difficulty: "Expert",
      items: [
        ["Ace\u2660", "3\u2665", "King\u2666", "7\u2663", "Queen\u2660", "2\u2665", "Jack\u2666", "9\u2663"],
        ["5\u2660", "Queen\u2665", "8\u2666", "Ace\u2663", "10\u2660", "4\u2665", "King\u2666", "6\u2663"],
        ["Jack\u2660", "7\u2665", "2\u2666", "Queen\u2663", "9\u2660", "Ace\u2665", "5\u2666", "King\u2663"],
        ["3\u2660", "10\u2665", "Jack\u2666", "4\u2663", "8\u2660", "6\u2665", "Ace\u2666", "Queen\u2663"]
      ]
    }
  },
  language: {
    spanish: {
      name: "Spanish Vocabulary",
      description: "Learn Spanish words using memory techniques",
      difficulty: "Beginner",
      items: [
        ["Casa = House", "Perro = Dog", "Gato = Cat", "Agua = Water", "Fuego = Fire"],
        ["Libro = Book", "Mesa = Table", "Silla = Chair", "Ventana = Window", "Puerta = Door"],
        ["Comida = Food", "Carne = Meat", "Pan = Bread", "Leche = Milk", "Fruta = Fruit"],
        ["Rojo = Red", "Azul = Blue", "Verde = Green", "Amarillo = Yellow", "Negro = Black"]
      ]
    },
    french: {
      name: "French Vocabulary",
      description: "Master French words with associations",
      difficulty: "Intermediate",
      items: [
        ["Maison = House", "Chien = Dog", "Chat = Cat", "Eau = Water", "Feu = Fire"],
        ["Livre = Book", "Table = Table", "Chaise = Chair", "Fen\u00EAtre = Window", "Porte = Door"],
        ["Nourriture = Food", "Viande = Meat", "Pain = Bread", "Lait = Milk", "Fruit = Fruit"],
        ["Rouge = Red", "Bleu = Blue", "Vert = Green", "Jaune = Yellow", "Noir = Black"]
      ]
    },
    german: {
      name: "German Vocabulary",
      description: "Learn German through memory techniques",
      difficulty: "Advanced",
      items: [
        ["Haus = House", "Hund = Dog", "Katze = Cat", "Wasser = Water", "Feuer = Fire"],
        ["Buch = Book", "Tisch = Table", "Stuhl = Chair", "Fenster = Window", "T\u00FCr = Door"],
        ["Essen = Food", "Fleisch = Meat", "Brot = Bread", "Milch = Milk", "Frucht = Fruit"],
        ["Rot = Red", "Blau = Blue", "Gr\u00FCn = Green", "Gelb = Yellow", "Schwarz = Black"]
      ]
    },
    japanese: {
      name: "Japanese Vocabulary",
      description: "Master Japanese words with visual associations",
      difficulty: "Expert",
      items: [
        ["Ie = House", "Inu = Dog", "Neko = Cat", "Mizu = Water", "Hi = Fire"],
        ["Hon = Book", "Teeburu = Table", "Isu = Chair", "Mado = Window", "Doa = Door"],
        ["Tabemono = Food", "Niku = Meat", "Pan = Bread", "Miruku = Milk", "Kudamono = Fruit"],
        ["Akai = Red", "Aoi = Blue", "Midori = Green", "Kiiroi = Yellow", "Kuroi = Black"]
      ]
    }
  },
  speech: {
    quotes: {
      name: "Famous Quotes",
      description: "Memorize inspiring quotations",
      difficulty: "Beginner",
      items: [
        ["To be or not to be", "I have a dream", "Ask not what your country", "That's one small step"],
        ["Four score and seven", "We hold these truths", "Give me liberty", "I think therefore I am"],
        ["The only thing we have", "Yesterday is history", "Life is what happens", "Be the change you wish"],
        ["In the end we will", "Float like a butterfly", "Imagination is more important", "Stay hungry stay foolish"]
      ]
    },
    poems: {
      name: "Poetry Lines",
      description: "Master poetic verses and stanzas",
      difficulty: "Intermediate",
      items: [
        ["Shall I compare thee", "Two roads diverged", "Because I could not", "How do I love thee"],
        ["Tiger tiger burning bright", "Once upon a midnight", "If you can keep your head", "Do not go gentle"],
        ["I wandered lonely as", "The woods are lovely", "Hope is the thing", "I am the master"],
        ["Whose woods these are", "Water water everywhere", "It was the best of times", "Call me Ishmael"]
      ]
    },
    speeches: {
      name: "Speech Segments",
      description: "Memorize presentation and speech content",
      difficulty: "Advanced",
      items: [
        ["Ladies and gentlemen", "Thank you for coming", "Today I want to talk", "In conclusion let me say"],
        ["The data shows clearly", "Our research indicates", "The results demonstrate", "This proves without doubt"],
        ["First let me address", "Moving on to the next", "This brings us to", "Finally I would like"],
        ["Good morning everyone", "I'm honored to be here", "The topic today is", "Questions are welcome"]
      ]
    },
    presentations: {
      name: "Technical Presentations",
      description: "Master complex presentation content",
      difficulty: "Expert",
      items: [
        ["Quarterly revenue increased", "Market share expanded", "Customer satisfaction improved", "Operational efficiency gained"],
        ["Key performance indicators", "Return on investment", "Strategic initiatives launched", "Competitive advantage maintained"],
        ["Digital transformation completed", "Innovation pipeline strengthened", "Talent acquisition successful", "Brand recognition enhanced"],
        ["Sustainability goals achieved", "Risk management improved", "Stakeholder engagement increased", "Future outlook remains positive"]
      ]
    }
  },
  palace: {
    home: {
      name: "Home Memory Palace",
      description: "Use your house as a memory location",
      difficulty: "Beginner",
      items: [
        ["Front door", "Living room", "Kitchen", "Bedroom", "Bathroom"],
        ["Garage", "Backyard", "Dining room", "Office", "Basement"],
        ["Attic", "Closet", "Balcony", "Laundry room", "Pantry"],
        ["Hallway", "Stairs", "Window", "Fireplace", "Garden"]
      ]
    },
    journey: {
      name: "Journey Method",
      description: "Create a mental journey for memorization",
      difficulty: "Intermediate",
      items: [
        ["Bus stop", "Coffee shop", "Park bench", "Library", "Grocery store"],
        ["School gate", "Gym entrance", "Bank counter", "Post office", "Restaurant"],
        ["Hospital lobby", "Church steps", "Movie theater", "Gas station", "Hotel lobby"],
        ["Airport gate", "Train station", "Shopping mall", "Beach pier", "Mountain trail"]
      ]
    },
    historical: {
      name: "Historical Locations",
      description: "Use famous places as memory anchors",
      difficulty: "Advanced",
      items: [
        ["Pyramids of Giza", "Colosseum Rome", "Great Wall China", "Machu Picchu", "Stonehenge"],
        ["Taj Mahal", "Eiffel Tower", "Statue of Liberty", "Big Ben", "Sydney Opera"],
        ["Mount Rushmore", "Golden Gate Bridge", "Niagara Falls", "Grand Canyon", "Yellowstone"],
        ["Buckingham Palace", "White House", "Red Square", "Forbidden City", "Acropolis"]
      ]
    },
    abstract: {
      name: "Abstract Concepts",
      description: "Place abstract ideas in memory locations",
      difficulty: "Expert",
      items: [
        ["Justice = Courthouse", "Love = Heart", "Freedom = Eagle", "Peace = Dove", "Wisdom = Owl"],
        ["Courage = Lion", "Hope = Sunrise", "Fear = Shadow", "Joy = Rainbow", "Anger = Storm"],
        ["Truth = Mirror", "Beauty = Rose", "Strength = Mountain", "Patience = Tree", "Creativity = Paint"],
        ["Knowledge = Library", "Success = Summit", "Failure = Valley", "Growth = Seedling", "Change = Butterfly"]
      ]
    }
  },
  rhyme: {
    basic: {
      name: "Basic Rhyme System",
      description: "Number rhymes for memorization",
      difficulty: "Beginner",
      items: [
        ["One = Sun", "Two = Shoe", "Three = Tree", "Four = Door", "Five = Hive"],
        ["Six = Sticks", "Seven = Heaven", "Eight = Gate", "Nine = Wine", "Ten = Hen"],
        ["Eleven = Leaven", "Twelve = Shelve", "Thirteen = Thirteen", "Fourteen = Fourteen", "Fifteen = Fifteen"],
        ["Sixteen = Sixteen", "Seventeen = Seventeen", "Eighteen = Eighteen", "Nineteen = Nineteen", "Twenty = Twenty"]
      ]
    },
    stories: {
      name: "Rhyming Stories",
      description: "Create memorable rhyming narratives",
      difficulty: "Intermediate",
      items: [
        ["Cat in the hat", "Sat on the mat", "Ate from the bat", "Chased the rat"],
        ["Dog in the fog", "Jumped on the log", "Ran like a cog", "Swam like a frog"],
        ["Bird in the sky", "Learned how to fly", "Winked with its eye", "Said its goodbye"],
        ["Fish in the sea", "Swam wild and free", "Climbed up a tree", "Buzzed like a bee"]
      ]
    },
    complex: {
      name: "Complex Rhymes",
      description: "Advanced rhyming patterns",
      difficulty: "Advanced",
      items: [
        ["Hickory dickory dock", "The mouse ran up the clock", "The clock struck one", "The mouse ran down"],
        ["Mary had a little lamb", "Its fleece was white as snow", "Everywhere that Mary went", "The lamb was sure to go"],
        ["Twinkle twinkle little star", "How I wonder what you are", "Up above the world so high", "Like a diamond in the sky"],
        ["Humpty Dumpty sat on wall", "Humpty Dumpty had great fall", "All the kings horses", "All the kings men"]
      ]
    },
    custom: {
      name: "Custom Rhymes",
      description: "Create your own rhyming systems",
      difficulty: "Expert",
      items: [
        ["Apple = Chapel", "Banana = Cabana", "Orange = Storage", "Grape = Tape"],
        ["Monday = Sundae", "Tuesday = Newsday", "Wednesday = Wendsday", "Thursday = Cursday"],
        ["January = Canary", "February = Library", "March = Arch", "April = Peril"],
        ["Science = Clients", "History = Mystery", "Math = Bath", "Art = Heart"]
      ]
    }
  }
};

export const MEMORY_TIPS = {
  link: [
    "Create vivid, unusual mental images",
    "Make associations action-packed and emotional",
    "Use all your senses in the mental pictures",
    "Practice the chain forwards and backwards",
    "Start with familiar locations or objects"
  ],
  number: [
    "Convert numbers to consonant sounds (Major System)",
    "Turn digit strings into memorable words",
    "Use chunking to break long numbers apart",
    "Create stories around number sequences",
    "Practice with phone numbers and dates first"
  ],
  peg: [
    "Master the basic peg words first (1=tie, 2=Noah, etc.)",
    "Create strong visual connections between pegs and items",
    "Use action and interaction in your mental images",
    "Practice the peg words until they're automatic",
    "Extend to higher numbers once basics are solid"
  ],
  major: [
    "Learn the consonant-number conversions by heart",
    "Practice converting numbers to words daily",
    "Use familiar words whenever possible",
    "Create logical word chains for long numbers",
    "Master 0-99 before attempting longer sequences"
  ],
  nameface: [
    "Focus on the most prominent facial feature",
    "Create meaningful associations with the name",
    "Use rhyming, wordplay, or visual connections",
    "Practice with people you meet regularly",
    "Review and reinforce the associations often"
  ],
  cards: [
    "Assign consistent personalities to each card",
    "Use suit associations (hearts=love, spades=death)",
    "Create storylines linking cards together",
    "Practice with smaller sequences first",
    "Use the Major System for advanced memorization"
  ],
  language: [
    "Create visual associations between words",
    "Use cognates and similar-sounding words",
    "Link new words to familiar concepts",
    "Practice with everyday vocabulary first",
    "Use the Link System to connect related words"
  ],
  speech: [
    "Break content into meaningful chunks",
    "Create visual scenes for each section",
    "Use the Journey Method for longer speeches",
    "Practice transitions between sections",
    "Record yourself to identify weak points"
  ],
  palace: [
    "Choose familiar, well-known locations",
    "Follow a consistent path through your palace",
    "Place items at specific, memorable spots",
    "Use interactive visualizations at each location",
    "Practice the route until it's automatic"
  ],
  rhyme: [
    "Master the basic number rhymes first",
    "Create memorable rhyming stories",
    "Use rhythm and meter to aid recall",
    "Practice with simple rhyming patterns",
    "Extend to more complex rhyming schemes"
  ]
};

export const TUTORIALS = {
  link: {
    title: "Link System Tutorial",
    intro: "The Link System is a powerful memory technique that connects items together through vivid mental associations. Here's how it works:",
    steps: [
      {
        title: "Create Vivid Images",
        description: "Turn each item into a clear, vivid mental image. The more unusual, emotional, or sensory the image, the better it will stick.",
        example: "Milk → Imagine a glowing white river flowing, cold and refreshing."
      },
      {
        title: "Make Associations",
        description: "Connect each image to the next with an action or interaction. Make the connection as vivid and memorable as possible.",
        example: "Bread → Picture the milk river soaking a giant loaf of bread, making it swell and float away."
      },
      {
        title: "Use Action and Emotion",
        description: "Add movement and emotion to your links. A story with action is far more memorable than static images.",
        example: "Eggs → See the floating bread crack open and spew eggs that bounce like rubber balls."
      },
      {
        title: "Review Your Chain",
        description: "Go through your linked chain once or twice to reinforce the connections.",
        example: "Walk through: Milk river → soaking bread → cracking eggs. Repeat forward and backward."
      }
    ],
    exampleSequence: ["Milk", "Bread", "Eggs", "Bananas", "Cheese"],
    exampleLink: "Imagine a glowing milk river soaking a giant loaf of bread. The bread swells and cracks open, releasing eggs that bounce like rubber balls. One egg hits a bunch of bananas, sending them flying. They land in a wheel of cheese that starts rolling away."
  },
  number: {
    title: "Number Memorization Tutorial",
    intro: "Memorizing numbers becomes easy when you transform them into memorable forms. Here's a simple approach:",
    steps: [
      {
        title: "Chunk the Numbers",
        description: "Break long numbers into smaller groups that are easier to remember.",
        example: "Phone: 555-0123 → Think of '555' as a repeating pattern, then '0123' as counting up."
      },
      {
        title: "Create Patterns",
        description: "Look for patterns in the numbers - sequences, repeats, or symmetries.",
        example: "Dates: 'July 4, 1776' → Think of Independence Day, with '1776' as the famous year."
      },
      {
        title: "Use Storytelling",
        description: "Create a story where each number plays a role. Numbers can represent sizes, times, or quantities in your story.",
        example: "555-0456 → Imagine 5 people (555) waiting for 4 hours (04) at a restaurant, ordering 56 drinks."
      },
      {
        title: "Practice with Familiar Numbers",
        description: "Start with phone numbers, dates, and addresses you encounter daily.",
        example: "Memorize your own phone number, then try memorizing friends' numbers."
      }
    ],
    exampleSequence: ["555-0123", "555-0456", "555-0789"],
    exampleLink: "Imagine 555 people standing in a line. Person #0123 opens a door to find 555 more people inside waiting for 4 hours. They finally enter and order 555 more drinks labeled 0789."
  },
  peg: {
    title: "Peg System Tutorial",
    intro: "The Peg System uses pre-memorized number-word pairs as 'pegs' to hang information. Here's how to use it:",
    steps: [
      {
        title: "Memorize Your Pegs",
        description: "First, learn the basic peg words: 1=tie, 2=Noah (ark), 3=tree, 4=door, 5=hive, etc.",
        example: "1 = tie (you wear a tie), 2 = Noah (built an ark), 3 = tree (grows from the ground)"
      },
      {
        title: "Visualize the Peg",
        description: "When you need to remember item #1, visualize it interacting with a tie.",
        example: "To remember 'apple' as item #1, picture an apple hanging from a necktie."
      },
      {
        title: "Create Strong Associations",
        description: "Make the connection between the peg and the item as vivid as possible with action and emotion.",
        example: "To remember 'book' as item #2, picture Noah opening the ark door and finding a giant book on the deck."
      },
      {
        title: "Practice in Order",
        description: "Recall your items by going through the peg numbers in sequence.",
        example: "1 (tie with apple), 2 (ark with book), 3 (tree with car)..."
      }
    ],
    exampleSequence: ["Apple", "Book", "Car", "Dog", "Egg"],
    exampleLink: "1 (Tie): Apple hanging from a silk tie. 2 (Noah): Noah opening the ark to a giant book. 3 (Tree): A car parked under a tree. 4 (Door): A dog barking through a keyhole. 5 (Hive): A beehive shaped like an egg."
  },
  major: {
    title: "Major System Tutorial",
    intro: "The Major System converts numbers to consonant sounds, which you then turn into words. Here's how to get started:",
    steps: [
      {
        title: "Learn the Consonant Codes",
        description: "Memorize which numbers correspond to which sounds: 0=s/z, 1=t/d, 2=n, 3=r, 4=s, 5=l, 6=j/sh/ch, 7=k/g, 8=f/v, 9=p/b.",
        example: "1 = t or d (down has 1 line), 4 = r (four has two r's)"
      },
      {
        title: "Convert Numbers to Sounds",
        description: "Replace each digit with its corresponding consonant sound, ignoring vowels.",
        example: "17 = t + k = 'tick' or 'duck'. 42 = s + n = 'sin' or 'sun'."
      },
      {
        title: "Create Memorable Words",
        description: "Turn the consonant sounds into real words by adding vowels. The more unusual, the better.",
        example: "10 = t + s = 'tie' or 'toes'. 47 = s + k = 'sack' or 'sick'."
      },
      {
        title: "Use the Link System",
        description: "Once you have words for numbers, use the Link System to remember sequences.",
        example: "17-42-47 = tick-sin-sack = picture a tick sneezing and making a sin, then putting it in a sack."
      }
    ],
    exampleSequence: ["17", "42", "47", "93", "20"],
    exampleLink: "17 (tick): A tick sneezes. 42 (sin): The sneeze is a mortal sin. 47 (sack): You put the sin in a sack. 93 (park): You run to the park. 20 (noah): Noah sees you running."
  },
  nameface: {
    title: "Name-Face Association Tutorial",
    intro: "Remembering names is a common challenge. This technique links names to facial features through creative associations:",
    steps: [
      {
        title: "Focus on the Feature",
        description: "Look for the most distinctive feature on someone's face - shape, size, or something unique.",
        example: "Person with large ears, or someone with a prominent nose, or high cheekbones."
      },
      {
        title: "Create a Wordplay Link",
        description: "Make a connection between the name and the feature using rhyme, sound, or meaning.",
        example: "Name 'Nosey' with a prominent nose. Name 'Earl' with large ears. Name 'Brow' with thick eyebrows."
      },
      {
        title: "Add Visual Interaction",
        description: "Imagine the feature and name interacting in a memorable way.",
        example: "For 'Nancy' with a round face, picture a round 'N' on her cheek that says 'Nancy!'"
      },
      {
        title: "Review and Reinforce",
        description: "Quickly review names and faces at the end of the day to solidify the associations.",
        example: "Think of each person's face and say their name aloud in your mind."
      }
    ],
    exampleSequence: ["John", "Sarah", "Michael", "Emily", "David"],
    exampleLink: "John (strong jaw): Picture John Travolta with a jaw that snaps shut like a trap. Sarah (sarah bird): Picture a bird named Sarah sitting on the person's head. Michael (high cheekbones): Picture Michael Jackson's iconic cheekbones glowing. Emily (smiley): Picture Emily's wide smile with 'Em' written on her teeth. David (dove): Picture a dove perched on the person's shoulder."
  },
  cards: {
    title: "Playing Card Memory Tutorial",
    intro: "Memorizing playing cards requires assigning personalities and creating storylines. Here's how:",
    steps: [
      {
        title: "Assign Card Personalities",
        description: "Give each card a consistent personality based on suit and rank. Hearts = love/creativity, Diamonds = money/energy, Clubs = action/intellect, Spades = death/mystery.",
        example: "Ace of Hearts = A romantic hero. King of Spades = A mysterious figure. Queen of Diamonds = A wealthy businesswoman."
      },
      {
        title: "Create Interactions",
        description: "When you see a sequence of cards, imagine these personalities interacting in a scene.",
        example: "Ace of Hearts meets King of Spades = A romantic hero encounters a mysterious stranger."
      },
      {
        title: "Build Storylines",
        description: "Link multiple cards together into a coherent story with action and emotion.",
        example: "Ace of Hearts -> Queen of Diamonds -> King of Spades = The hero meets a wealthy woman who introduces him to a mysterious figure."
      },
      {
        title: "Use the Major System for Position",
        description: "For longer sequences, use the Major System to number positions and link to card images.",
        example: "Position 1 (tie) with Ace of Hearts = A necktie with a heart图案."
      }
    ],
    exampleSequence: ["Ace of Hearts", "King of Spades", "Queen of Diamonds", "Jack of Clubs", "Ten of Hearts"],
    exampleLink: "A romantic hero (Ace of Hearts) meets a mysterious figure (King of Spades) at a bank (Diamonds) where he meets a businesswoman (Queen of Diamonds). They argue (Clubs) with a jogger (Jack) while a ten-dollar bill (Ten of Hearts) floats between them."
  },
  language: {
    title: "Foreign Language Vocabulary Tutorial",
    intro: "Learning vocabulary is easier when you create strong visual associations with familiar concepts:",
    steps: [
      {
        title: "Create Visual Associations",
        description: "Link each new word to a vivid mental image. Use the word's sound, meaning, or both.",
        example: "Spanish 'perro' (dog): Picture a dog barking 'perro perro'."
      },
      {
        title: "Use Cognates",
        description: "Leverage words that sound similar to English and have similar meanings.",
        example: "French 'librairie' (bookstore) sounds like 'library'. Picture books everywhere."
      },
      {
        title: "Make Connections",
        description: "Link new words to words you already know, creating a web of associations.",
        example: "Learn 'apple' in Spanish (manzana), then learn 'red' (rojo) and picture a red apple with 'manzana roja'."
      },
      {
        title: "Practice in Context",
        description: "Learn words in phrases or sentences rather than in isolation.",
        example: "Learn 'I eat apples' instead of just 'apple'. Picture yourself eating an apple saying the full sentence."
      },
      {
        title: "Use the Link System",
        description: "Chain related words together using the Link System for lists.",
        example: "Milk (leche) -> Bread (pan) -> Cheese (queso): Picture leche turning into pan turning into queso."
      }
    ],
    exampleSequence: ["Milk", "Bread", "Cheese", "Apple", "Water"],
    exampleLink: "Spanish: Leche (milk) is poured onto pan (bread) making it soggy, then queso (cheese) melts on top. An apple (manzana) rolls onto the table, and water (agua) spills everywhere."
  },
  speech: {
    title: "Text & Speech Memory Tutorial",
    intro: "Memorizing speeches and long texts requires chunking and visual scenes. Here's how:",
    steps: [
      {
        title: "Break into Chunks",
        description: "Divide the text into logical sections or paragraphs. Memorize one chunk at a time.",
        example: "A 10-minute speech might have 5-10 chunks of 30-60 seconds each."
      },
      {
        title: "Create Visual Scenes",
        description: "For each chunk, create a vivid visual scene that represents the content.",
        example: "If a chunk is about 'the importance of practice', picture a person practicing daily with visible progress."
      },
      {
        title: "Use the Journey Method",
        description: "Place each chunk at a location along a familiar path or journey.",
        example: "Start at your front door (intro), move to the living room (main point 1), kitchen (main point 2), etc."
      },
      {
        title: "Practice Transitions",
        description: "Focus on how one chunk flows into the next. Practice the transitions repeatedly.",
        example: "After finishing chunk 1, what's the first sentence of chunk 2? Practice this link."
      },
      {
        title: "Record and Review",
        description: "Record yourself speaking and listen back. Identify weak points and reinforce them.",
        example: "Record chunk by chunk, listen for where you stumble, and reinforce those visuals."
      }
    ],
    exampleSequence: ["Introduction", "Main Point 1", "Main Point 2", "Main Point 3", "Conclusion"],
    exampleLink: "Front door (Introduction): You welcome guests. Living room (Point 1): You sit and share your first story. Kitchen (Point 2): You prepare food while sharing point 2. Bedroom (Point 3): You rest and share point 3. Back door (Conclusion): You bid guests farewell."
  },
  palace: {
    title: "Memory Palace Tutorial",
    intro: "The Memory Palace technique uses familiar locations to store information. Here's how to build and use one:",
    steps: [
      {
        title: "Choose a Familiar Location",
        description: "Select a place you know well - your home, workplace, or a familiar route.",
        example: "Your childhood home, your daily commute, or your favorite park."
      },
      {
        title: "Establish a Clear Path",
        description: "Define a specific route through your location with specific stopping points.",
        example: "Front door → Hallway → Living room → Kitchen → Bedroom → Bathroom."
      },
      {
        title: "Place Items at Locations",
        description: "Put each item you want to remember at a specific location in your palace.",
        example: "Shopping list item 1 at the door mat, item 2 by the coat rack, etc."
      },
      {
        title: "Use Interactive Visualization",
        description: "Make the items interact with their locations. Make them unusual, emotional, and vivid.",
        example: "Milk carton at the door: Picture a giant milk carton blocking your entry."
      },
      {
        title: "Walk Through Your Palace",
        description: "Mentally walk through your palace to recall the items in order.",
        example: "Door → milk carton, hall → bread loaf, living room → eggs on sofa..."
      }
    ],
    exampleSequence: ["Milk", "Bread", "Eggs", "Cheese", "Butter"],
    exampleLink: "Front door: Milk carton blocking entry. Hallway: Loaves of bread lining the walls. Living room: Eggs balanced on the coffee table. Kitchen: Cheese melting on the stove. Bathroom: Butter soap in the sink."
  },
  rhyme: {
    title: "Rhyme System Tutorial",
    intro: "The Rhyme System uses rhyming words to remember numbers and information. Here's how:",
    steps: [
      {
        title: "Learn the Basic Rhymes",
        description: "Memorize rhymes for numbers: 1=sun, 2=shoe, 3=tree, 4=door, 5=hive, 6=sticks, 7=heaven, 8=gate, 9=wine, 10=hen.",
        example: "1 = sun (one bright sun), 2 = shoe (two shoes), 3 = tree (three branches)"
      },
      {
        title: "Create Rhyming Stories",
        description: "Link items to the rhyming words in a story that follows the rhyme pattern.",
        example: "Sun → Picture yourself basking in the sun. Shoe → Picture a giant shoe stepping on the sun."
      },
      {
        title: "Use Rhythm and Meter",
        description: "Create rhymes with rhythm and meter to make them easier to remember.",
        example: "One, two, three, four... rhyming patterns help recall through rhythm."
      },
      {
        title: "Build on Basic Patterns",
        description: "Start with simple rhymes, then create more complex rhyming schemes.",
        example: "Once you know basic rhymes, create rhyming couplets and longer verses."
      },
      {
        title: "Practice with Lists",
        description: "Use the rhyme system to memorize numbered lists of any kind.",
        example: "Top 5 goals: 1-Sun (career success shines like sun), 2-Shoe (new career path/shoe), etc."
      }
    ],
    exampleSequence: ["Sun", "Shoe", "Tree", "Door", "Hive"],
    exampleLink: "Bright sun in the sky (1). Giant shoe stepping on the sun (2). Tree growing out of the shoe (3). Door embedded in the tree (4). Beehive at the top of the door (5)."
  }
};
